import { useTranslations } from 'next-intl';
import { buildRoute, isUnlimitedLoyaltyLocale, isLoyaltyDeactivatedLocale, Locale } from '@headless-workspace/config';
import {
    GlowButton,
    GlowClickable,
    GlowSkeleton,
    GlowTextBody,
    GlowTitle,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component, ComponentMemo, IntlMessagesKeys } from '@headless-workspace/typings';
import { ConnectedUserInfo, isUserGold, RichText, useClientData } from '../../../../../src';
import { memo } from 'react';

type ConnectionBannerConnectedGreetingProps = PropsWithLocale & {
    userInfo: ConnectedUserInfo;
    isLoggingOut: boolean;
    isMySephoraActivated: boolean;
};

type LoyaltyProgramInfoPropsType = {
    locale: Locale;
    messageKey: IntlMessagesKeys;
    currentPoints: number;
    pointsToNextDiscount: number;
    linkText: string;
    isMySephoraActivated: boolean;
};

type LoyaltyProgramFeaturePropsType = {
    locale: Locale;
    messageKey: IntlMessagesKeys;
    linkText: string;
    isMySephoraActivated: boolean;
};

type LoyaltyProgramCtaPropsType = {
    locale: Locale;
    linkText: string;
    isMySephoraActivated: boolean;
};

// Define reusable components for better readability
const LoyaltyProgramInfo: Component<LoyaltyProgramInfoPropsType> = ({
    locale,
    messageKey,
    currentPoints,
    pointsToNextDiscount,
    linkText,
    isMySephoraActivated,
}) => (
    <>
        <RichText messageKey={messageKey} fontSize="body" values={{ currentPoints, pointsToNextDiscount }} />
        <LoyaltyProgramCta locale={locale} linkText={linkText} isMySephoraActivated={isMySephoraActivated} />
    </>
);

const LoyaltyProgramFeature: Component<LoyaltyProgramFeaturePropsType> = ({
    messageKey,
    linkText,
    locale,
    isMySephoraActivated,
}) => (
    <>
        <RichText messageKey={messageKey} fontSize="body" className="whitespace-pre-line" />
        <LoyaltyProgramCta locale={locale} linkText={linkText} isMySephoraActivated={isMySephoraActivated} />
    </>
);

const LoyaltyProgramCta: Component<LoyaltyProgramCtaPropsType> = ({ linkText, locale, isMySephoraActivated }) => {
    const href = buildRoute(locale, 'loyaltyProgram');

    return isMySephoraActivated ? (
        <GlowButton
            className="self-stretch transition-backgroundOutline"
            href={href}
            label={linkText}
            variant="secondary"
        />
    ) : (
        <GlowClickable
            href={href}
            content={{
                labelElement: <GlowTextBody text={linkText} textDecoration="underline" />,
            }}
        />
    );
};

export const ConnectionBannerConnectedGreeting: ComponentMemo<ConnectionBannerConnectedGreetingProps> = memo(({
    locale,
    isLoggingOut,
    userInfo,
    isMySephoraActivated,
}) => {
    // feature flipping to hide/display MySephora greeting mention based on .env MYSEPHORA_HIDE_LOGGED_GREETING_MENTION
    const { getClientFlags } = useClientData();
    const shouldHideMySephoraGreeting = getClientFlags('isMySephoraGreetingMentionHidden');
    const displayMySephoraGreetingMention = !(isMySephoraActivated && shouldHideMySephoraGreeting);

    const shouldDisplayGreetingMention = !isLoyaltyDeactivatedLocale(locale) && displayMySephoraGreetingMention;

    const t = useTranslations('Header.connection');
    const isUnlimitedGold = userInfo && isUnlimitedLoyaltyLocale(locale) && isUserGold(userInfo);
    const loyaltyProgramText =
        isMySephoraActivated || isUnlimitedGold
            ? 'Header.connection.banner.label.loyaltyProgramPointsAlternative'
            : 'Header.connection.banner.label.loyaltyProgramPoints';

    const loyaltyProgramLinkText = isMySephoraActivated
        ? 'banner.label.loyaltyProgramEnrolledAlternative'
        : 'banner.label.loyaltyProgramEnrolled';

    const featureLoyaltyProgramText = isMySephoraActivated
        ? 'Header.connection.banner.label.loyaltyProgramFeatureAlternative'
        : 'Header.connection.banner.label.loyaltyProgramFeature';

    const loyaltyProgramNotEnrolledText = isMySephoraActivated
        ? t('banner.label.loyaltyProgramNotEnrolledAlternative')
        : t('banner.label.loyaltyProgramNotEnrolled');

    if (isLoggingOut) {
        return (
            <>
                <GlowSkeleton width={'17.875rem'} height={'2rem'} />
                <GlowSkeleton width={'100%'} height={'1.25rem'} containerClassName={'w-full'} line={3} />
                <GlowSkeleton height={'1.25rem'} containerClassName={'w-eighty'} />
            </>
        );
    }

    return (
        <>
            <GlowTitle text={t('banner.label.greetingConnected', { name: userInfo.firstName })} fontSize="title4" />
            {shouldDisplayGreetingMention &&
                (userInfo.loyalty ? (
                    <LoyaltyProgramInfo
                        locale={locale}
                        messageKey={loyaltyProgramText}
                        currentPoints={userInfo.loyalty.currentPoints}
                        pointsToNextDiscount={userInfo.loyalty.pointsToNextDiscount}
                        linkText={t(loyaltyProgramLinkText)}
                        isMySephoraActivated={isMySephoraActivated}
                    />
                ) : (
                    <LoyaltyProgramFeature
                        locale={locale}
                        messageKey={featureLoyaltyProgramText}
                        linkText={loyaltyProgramNotEnrolledText}
                        isMySephoraActivated={isMySephoraActivated}
                    />
                ))}
        </>
    );
});